/**
 * TanStack Query Hook for Industry Breakdown Tree KPI
 *
 * This hook provides access to the industry breakdown tree KPI data from the database.
 * It uses TanStack Query for efficient data fetching with automatic caching,
 * background refetching, and error handling.
 *
 * Database Source: web.v_kpi_industry_breakdown_tree_v1 view
 *
 * @module useKpiIndustryBreakdownTreeV1
 * @category Database Hooks
 *
 * @example
 * ```typescript
 * // Basic usage in a component
 * import { useKpiIndustryBreakdownTreeV1 } from '@/hooks/database/useKpiIndustryBreakdownTreeV1';
 *
 * function IndustryBreakdownChart() {
 *   const { data, isLoading, error } = useKpiIndustryBreakdownTreeV1();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       <h2>Industry Breakdown</h2>
 *       {data.map((item) => (
 *         <div key={item.industry}>
 *           {item.industry}: {item.company_count}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Advanced usage with refetch control
 * function KpiDashboard() {
 *   const { data, isLoading, error, refetch, isFetching } = useKpiIndustryBreakdownTreeV1();
 *
 *   const handleRefresh = () => {
 *     refetch();
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleRefresh} disabled={isFetching}>
 *         {isFetching ? 'Refreshing...' : 'Refresh Data'}
 *       </button>
 *       {data && <IndustryChart data={data} />}
 *     </div>
 *   );
 * }
 * ```
 */

import { useQuery, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { KpiIndustryBreakdownItem } from '@/types/kpi.types';

/**
 * Fetches industry breakdown tree KPI data from the database
 *
 * This function queries the web.v_kpi_industry_breakdown_tree_v1 view and returns
 * an array of industry breakdown items with company counts.
 *
 * @returns {Promise<KpiIndustryBreakdownItem[]>} Array of KPI data objects with industry breakdown
 * @throws {Error} If the database query fails or returns invalid data
 *
 * @internal
 */
const fetchKpiIndustryBreakdownTree = async (): Promise<KpiIndustryBreakdownItem[]> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_industry_breakdown_tree_v1')
    .select('*');
  // NOTE: No .single() - returns array

  if (error) {
    throw new Error(`Failed to fetch industry breakdown tree KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from industry breakdown tree KPI query');
  }

  return data;
};

/**
 * React Hook for Industry Breakdown Tree KPI Data
 *
 * Fetches and caches the industry breakdown tree KPI data using TanStack Query.
 * The data is cached for 1 hour and will not automatically refetch on
 * window focus or component mount.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'industry-breakdown-tree', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiIndustryBreakdownItem[]>} TanStack Query result object
 * @property {KpiIndustryBreakdownItem[] | undefined} data - The KPI data array (undefined during loading)
 * @property {boolean} isLoading - True if query is in initial loading state
 * @property {boolean} isFetching - True if query is fetching (including background fetches)
 * @property {boolean} isError - True if query encountered an error
 * @property {Error | null} error - The error object if query failed
 * @property {Function} refetch - Function to manually refetch the data
 *
 * @example
 * ```typescript
 * // Simple usage
 * const { data, isLoading } = useKpiIndustryBreakdownTreeV1();
 * console.log(data?.length); // e.g., 15
 * console.log(data?.[0]); // { industry: 'Tech', company_count: 250 }
 * ```
 *
 * @example
 * ```typescript
 * // Error handling
 * const { data, isLoading, isError, error } = useKpiIndustryBreakdownTreeV1();
 *
 * if (isLoading) {
 *   return <Spinner />;
 * }
 *
 * if (isError) {
 *   return <ErrorAlert message={error?.message} />;
 * }
 *
 * return <TreeChart data={data} />;
 * ```
 *
 * @example
 * ```typescript
 * // Manual refetch
 * const { data, refetch } = useKpiIndustryBreakdownTreeV1();
 *
 * const handleUpdate = async () => {
 *   await refetch();
 *   toast.success('KPI data refreshed');
 * };
 * ```
 */
export const useKpiIndustryBreakdownTreeV1 = (): UseQueryResult<KpiIndustryBreakdownItem[]> => {
  return useQuery<KpiIndustryBreakdownItem[]>({
    queryKey: ['kpi', 'industry-breakdown-tree', 'v1'],
    queryFn: fetchKpiIndustryBreakdownTree,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};

/**
 * Query Key Factory for Industry Breakdown Tree KPI
 *
 * Provides consistent query key generation for TanStack Query.
 * Useful for manual cache manipulation or query invalidation.
 *
 * @returns {string[]} The query key array
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { getKpiIndustryBreakdownTreeQueryKey } from '@/hooks/database/useKpiIndustryBreakdownTreeV1';
 *
 * function RefreshButton() {
 *   const queryClient = useQueryClient();
 *
 *   const invalidateCache = () => {
 *     queryClient.invalidateQueries({
 *       queryKey: getKpiIndustryBreakdownTreeQueryKey()
 *     });
 *   };
 *
 *   return <button onClick={invalidateCache}>Clear Cache</button>;
 * }
 * ```
 */
export const getKpiIndustryBreakdownTreeQueryKey = (): string[] => {
  return ['kpi', 'industry-breakdown-tree', 'v1'];
};

/**
 * Prefetch Function for Industry Breakdown Tree KPI
 *
 * Allows prefetching the KPI data before component mount.
 * Useful for optimistic data loading and improving perceived performance.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - TanStack Query client instance
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { prefetchKpiIndustryBreakdownTree } from '@/hooks/database/useKpiIndustryBreakdownTreeV1';
 *
 * function App() {
 *   const queryClient = useQueryClient();
 *
 *   useEffect(() => {
 *     // Prefetch KPI data on app load
 *     prefetchKpiIndustryBreakdownTree(queryClient);
 *   }, [queryClient]);
 *
 *   return <Dashboard />;
 * }
 * ```
 */
export const prefetchKpiIndustryBreakdownTree = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.prefetchQuery({
    queryKey: getKpiIndustryBreakdownTreeQueryKey(),
    queryFn: fetchKpiIndustryBreakdownTree,
    staleTime: 3600000,
  });
};
